<template>
	<Pupup ref="root" @close="close" :showClose="true" :title="title" :width="800">
		<template v-slot:content>
			<div :class="{ container: true, wistia: type === 'wistia' }">
				<iframe
					v-if="type !== 'wistia'"
					:src="embedUrl"
					frameborder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowfullscreen />
				<iframe
					v-if="type === 'wistia'"
					:src="'https://fast.wistia.net/embed/iframe/' + videoId + '?seo=true&videoFoam=true&autoPlay=' + (autoPlay ? 'true' : '')"
					:title="title"
					:allow="allow"
					allowtransparency="true"
					frameborder="0"
					scrolling="no"
					class="wistia_embed"
					name="wistia_embed"
					msallowfullscreen
					width="100%"
					height="100%"></iframe>
			</div>
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import ApiService from "./../services/api.service";
import Pupup from "./Display/Pupup.vue";

export default {
	name: "VideoPlayer",
	components: {
		Pupup,
	},
	props: {
		title: {
			type: String,
			default: "",
		},
		type: {
			type: String,
			default: "youtube",
		},
		videoId: {
			type: String,
			default: "youtube",
		},
		playlist: {
			type: Boolean,
			default: false,
		},
		customPlaylist: {
			type: Array,
			default: () => [],
		},
		autoPlay: {
			type: Boolean,
			default: true,
		},
	},
	emits: ["close"],
	setup(props, { emit }) {
		const { proxy } = getCurrentInstance();
		const store = proxy.$store;

		// --- Template Refs ---
		const root = ref(null);

		// --- State ---
		const allows = ref(["fullscreen"]);

		// --- Vuex State ---
		const videos = computed(() => store.state.general.videos);

		// --- Computed ---
		const allow = computed(() => allows.value.join("; "));

		const embedUrl = computed(() => {
			switch (props.type) {
				case "youtube":
					if (props.customPlaylist.length > 0) {
						return "https://www.youtube.com/embed/" + props.videoId + "?modestbranding=1&playlist=" + props.customPlaylist.join(",");
					}
					return (
						(props.playlist ? "https://www.youtube.com/embed/videoseries?modestbranding=1&list=" : "https://www.youtube.com/embed/") +
						props.videoId
					);
				default:
					return "";
			}
		});

		// --- Methods ---
		const close = () => {
			emit("close");
		};

		const logPlay = () => {
			const v = videos.value.find((vid) => vid.videoId === props.videoId);
			if (v && !!v.slug) {
				ApiService.put("users/video-view/" + v.slug)
					.then(() => {})
					.catch(() => {});
			}
		};

		// --- Lifecycle Hooks ---
		onMounted(() => {
			if (props.autoPlay) {
				// The `root.value.$el` assumes Pupup exposes its root element.
				// A safer way is to use the iframe's ref if possible, but this works if Pupup's $el is the root.
				const iframe = root.value?.$el?.querySelector("iframe");
				if (iframe) {
					iframe.setAttribute("autoplay", "");
				}
				allows.value.push("autoplay");
			}

			if (props.type === "wistia") {
				let wistiaScript = document.createElement("script");
				wistiaScript.setAttribute("src", "https://fast.wistia.net/assets/external/E-v1.js");
				wistiaScript.async = true;
				document.body.appendChild(wistiaScript);
			}

			logPlay();
		});

		return {
			root,
			allow,
			embedUrl,
			close,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../assets/css/less/_helper.less";
@import "./../assets/css/less/_settings.less";

.container {
	position: relative;
	overflow: hidden;
	width: 100%;
	padding-top: 56.25%; /* 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
	&.wistia {
		//padding-top: 53.13%;
	}
	iframe {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		width: 100%;
		height: 100%;
		border: none;
	}
}
</style>